/**
 * Emissive engine trail (spec Section 7 + R2-M4 VFX-9): beat-reactive
 * brightness, styled by the equipped trail cosmetic — each style has its own
 * color, ribbon width, and translucency, so equipping one visibly changes
 * the ship's wake. On-beat jumps kick the brightness for one beat (VFX-8).
 * High/Med tiers only. Recreated on each run start so a retry teleport never
 * smears across the track.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TrailMesh } from "@babylonjs/core/Meshes/trailMesh";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { makeFlatMaterial } from "./scene";
import { TRAIL_DIAMETER, TRAIL_LENGTH } from "../config/tuning";

export interface TrailStyle {
  colorHex: string;
  /** Ribbon diameter multiplier (1 = default). */
  width?: number;
  alpha?: number;
}

export class ShipTrail {
  private trail: TrailMesh | null = null;
  private mat: StandardMaterial;
  private base: Color3;
  private enabled = true;
  private diameter = TRAIL_DIAMETER;
  private kickLeft = 0;

  constructor(
    private readonly scene: Scene,
    private readonly ship: Mesh,
  ) {
    this.mat = makeFlatMaterial(scene, "mat-trail", "#22D3EE", 0.8);
    this.mat.alpha = 0.6;
    this.base = this.mat.emissiveColor.clone();
  }

  setStyle(style: TrailStyle): void {
    this.mat.emissiveColor = Color3.FromHexString(style.colorHex).scale(0.8);
    this.base = this.mat.emissiveColor.clone();
    this.mat.alpha = style.alpha ?? 0.6;
    const next = TRAIL_DIAMETER * (style.width ?? 1);
    if (next !== this.diameter) {
      this.diameter = next;
      if (this.trail) this.restart(); // width is baked at creation
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.dispose();
  }

  /** Fresh trail at the ship's current position (run start / respawn). */
  restart(): void {
    this.dispose();
    if (!this.enabled) return;
    this.trail = new TrailMesh("ship-trail", this.ship, this.scene, this.diameter, TRAIL_LENGTH, true);
    this.trail.material = this.mat;
    this.trail.isPickable = false;
    this.kickLeft = 0;
  }

  /** On-beat jump reward: +20 % brightness for the next beat (VFX-8). */
  kick(beats = 1): void {
    this.kickLeft = beats;
  }

  /**
   * Beat-reactive brightness (subtle — the ring is the signature element).
   * @param beatPulse eased pulse 0..1
   * @param dtBeats how many beats elapsed since the last call (decays kick)
   */
  pulse(beatPulse: number, dtBeats = 0): void {
    if (!this.trail) return;
    this.kickLeft = Math.max(0, this.kickLeft - dtBeats);
    const kick = this.kickLeft > 0 ? 1.2 : 1;
    this.base.scaleToRef((0.7 + 0.5 * beatPulse) * kick, this.mat.emissiveColor);
  }

  hide(): void {
    this.dispose();
  }

  private dispose(): void {
    this.trail?.dispose();
    this.trail = null;
  }
}
